// --- FILE: src/pages/PinScreenPage.jsx (ESTILO AJUSTADO A TEMA OSCURO) ---
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LogOut, ArrowLeft, Shield, Wifi } from 'lucide-react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';

const PinScreenPage = () => {
  const { employeeLogin, logout, userRole, navigateToReports } = useAuth();
  const [pin, setPin] = useState('');
  const [feedback, setFeedback] = useState({ message: '', type: '' });
  const [isProcessing, setIsProcessing] = useState(false);
  const [company, setCompany] = useState({ name: 'NEW IN THE BOX', logoUrl: null });

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'config', 'company'), snap => {
      if (snap.exists()) {
        const d = snap.data();
        setCompany({ name: d.name || 'NEW IN THE BOX', logoUrl: d.logoUrl || null });
      }
    });
    return unsub;
  }, []);

  const handlePinSubmit = useCallback(async (currentPin) => {
    setIsProcessing(true);
    setFeedback({ message: '', type: '' });
    try {
      const success = await employeeLogin(currentPin);
      if (!success) {
        setFeedback({ message: 'PIN no válido', type: 'error' });
        setTimeout(() => {
          setPin('');
          setFeedback({ message: '', type: '' });
          setIsProcessing(false);
        }, 1200);
      }
    } catch (error) {
      console.error("Error en el proceso de login con PIN:", error);
      setFeedback({ message: 'Error de conexión', type: 'error' });
      setTimeout(() => {
        setPin('');
        setFeedback({ message: '', type: '' });
        setIsProcessing(false);
      }, 1500);
    }
  }, [employeeLogin]);

  useEffect(() => {
    if (pin.length === 4) {
      handlePinSubmit(pin);
    }
  }, [pin, handlePinSubmit]);

  const handleKeyPress = (key) => {
    if (pin.length < 4 && !isProcessing) {
      setPin(pin + key);
    }
  };
  
  const handleDelete = () => {
    if (!isProcessing) {
      setPin(pin.slice(0, -1));
    }
  };

  return (
    // Fondo con gradiente para coincidir con AdminReportPage
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white flex flex-col relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        {/* Elementos de fondo decorativos, ajustados para tonos oscuros */}
        <div className="absolute -top-32 -right-32 w-64 h-64 rounded-full bg-gradient-to-br from-white/3 to-transparent blur-3xl opacity-20"></div>
        <div className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-gradient-to-tr from-white/2 to-transparent blur-3xl opacity-20"></div>
      </div>

      <header className="relative z-10 w-full flex justify-between items-center p-6">
        {userRole === 'admin' ? (
          <button 
            onClick={navigateToReports} 
            className="p-3 text-white/60 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-300 backdrop-blur-sm"
            title="Volver al Panel"
          >
            <ArrowLeft size={24} />
          </button>
        ) : (
          <button 
            onClick={logout} 
            className="p-3 text-white/60 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-300 backdrop-blur-sm"
            title="Cerrar sesión"
          >
            <LogOut size={24} />
          </button>
        )}
        
        <div className="text-center">
          <div className="flex items-center justify-center mb-1">
            <img
              src={company.logoUrl || '/LogoNibpr.png'}
              alt={company.name}
              className={`h-12 w-auto mr-3 ${!company.logoUrl ? 'filter brightness-0 invert' : 'object-contain rounded'}`}
            />
          </div>
          <h1 className="text-2xl font-bold tracking-wide text-white">{company.name}</h1> {/* Asegurar texto blanco */}
        </div>
        
        <div className="w-12 h-12"></div>
      </header>

      <main className="relative z-10 flex flex-col items-center justify-center px-6 text-center py-2 flex-shrink-0">
        <p className="text-white/60 mb-5 text-center text-base">Ingresa tu PIN para continuar</p>
        
        <div className={`flex space-x-10 mb-1 transition-all duration-300 ${feedback.type === 'error' ? 'animate-pulse' : ''}`}>
          {[...Array(4)].map((_, i) => (
            <div 
              key={i} 
              className={`w-6 h-6 rounded-full border-2 transition-all duration-300 ${
                pin.length > i 
                  ? 'bg-blue-500 border-blue-500 shadow-lg shadow-blue-500/30' // Puntos azules para PIN ingresado
                  : 'border-white/30 bg-transparent'
              } ${
                feedback.type === 'error' && pin.length > i 
                  ? 'bg-red-500 border-red-500 shadow-red-500/30' // Puntos rojos para error
                  : ''
              }`} 
            />
          ))}
        </div>
        
        <div className="h-8 mb-1 flex items-center justify-center">
          {isProcessing && !feedback.message ? (
            <div className="flex items-center space-x-3 text-white/60">
              <div className="w-5 h-5 border-2 border-white/20 border-t-white/60 rounded-full animate-spin"></div>
              <span className="text-base">Verificando acceso...</span>
            </div>
          ) : (
            <span className={`text-base font-medium transition-all duration-300 ${feedback.type === 'error' ? 'text-red-400' : 'text-white/60'}`}>
              {feedback.message}
            </span>
          )}
        </div>
      </main>

      <footer className="relative z-10 w-full flex justify-center px-2 pb-6">
        <div className="max-w-lg">
          <div className="grid grid-cols-3 gap-3 justify-items-center">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
              <button
                key={num}
                onClick={() => handleKeyPress(num.toString())}
                disabled={isProcessing}
                className="h-25 w-25 bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl text-3xl font-light hover:bg-white/10 hover:border-white/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 shadow-lg hover:shadow-xl text-white" // Texto blanco
              >
                {num}
              </button>
            ))}
            
            <button
              onClick={handleDelete}
              disabled={isProcessing || pin.length === 0}
              className="h-25 w-25 bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl flex items-center justify-center hover:bg-white/10 hover:border-white/20 transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed active:scale-95 shadow-lg hover:shadow-xl"
            >
              <ArrowLeft size={28} className="text-white/80" />
            </button>
            
            <button
              onClick={() => handleKeyPress('0')}
              disabled={isProcessing}
              className="h-24 w-24 bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl text-3xl font-light hover:bg-white/10 hover:border-white/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 shadow-lg hover:shadow-xl text-white" // Texto blanco
            >
              0
            </button>
            
            <div></div> {/* Placeholder para centrar el 0 */}
          </div>
          
          <div className="mt-8 text-center">
            <div className="inline-flex items-center space-x-3 px-6 py-3 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-xl">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <Shield className="w-4 h-4 text-white/40" />
              <span className="text-white/60 text-sm font-medium">Terminal seguro</span>
              <Wifi className="w-4 h-4 text-white/40" />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PinScreenPage;